import Dexie from "dexie";
import { createDatabase } from "../Hooks/IndexedDBUtils";

const OFFLINE_QUEUES = [
  { name: "syncableContactDB", store: "syncableContactsStore" },
  { name: "syncableWolkingListDB", store: "syncableWalkingListStore" },
  { name: "syncableProfileDB", store: "syncableProfileStore" },
  { name: "syncableTrackingDB", store: "syncableTrackingStore" },
  { name: "surveyformDB", store: "surveyformStore" },
  { name: "syncableMeetingDB", store: "syncableMeetingStore" },
];

export const OFFLINE_SUBMISSIONS_KEY = "offlineSubmissions";

// syncOne must resolve truthy on success; a falsy result or a throw keeps the item queued for the next sync
export async function runSyncQueue(items, syncOne, { onItemSynced, delayMs = 1000 } = {}) {
  const syncedIds = [];
  let failedCount = 0;

  for (const item of items) {
    let ok = false;
    try {
      ok = !!(await syncOne(item));
    } catch (error) {
      console.error("Offline sync item failed:", error);
    }

    if (ok) {
      syncedIds.push(item?.id);
      if (onItemSynced) onItemSynced(item);
    } else {
      failedCount += 1;
    }

    if (delayMs > 0) await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  return { syncedIds, failedCount };
}

export async function countPendingOfflineData() {
  let count = 0;

  try {
    count += (JSON.parse(localStorage.getItem(OFFLINE_SUBMISSIONS_KEY)) || []).length;
  } catch {
    // unreadable value counts as nothing pending
  }

  for (const { name, store } of OFFLINE_QUEUES) {
    try {
      if (!(await Dexie.exists(name))) continue;
      count += await createDatabase(name, store)[store].count();
    } catch {
      // IndexedDB unavailable: nothing can be pending in it
    }
  }

  return count;
}

let discardPrompt = null;

// The themed prompt component registers itself here; returns an unregister function
export function registerDiscardPrompt(handler) {
  discardPrompt = handler;
  return () => {
    if (discardPrompt === handler) discardPrompt = null;
  };
}

// Resolves false when the user declines to lose unsynced offline changes
export async function confirmDiscardPendingOfflineData(action) {
  const pending = await countPendingOfflineData();
  if (pending === 0) return true;
  if (discardPrompt) return discardPrompt({ pending, action });
  return window.confirm(
    `You have ${pending} offline change(s) that have not been synced. They will be lost if you ${action}. Continue?`
  );
}
